// CUÁNTOS ALUMNOS HAY SUSCRITOS. Que no es cuántas filas hay.
//
// `user_subscriptions` es una tabla de COMPRAS, no de personas, y en ella
// conviven dos formas de guardar el mismo trimestre. Medido en producción el
// 4-oct-2026:
//
//   | filas de la tabla                            | 25 |
//   | alumnos que compraron alguna vez              | 16 |
//   | filas `activated`                             | 16 |
//   | ALUMNOS SUSCRITOS HOY                         | 14 |  ← el que importa
//   | `sum(term_months)` de la tabla entera         | 49 |
//
// Las tres formas de equivocarse, y por qué cada una miente:
//
//   · **Contar filas** (16) duplica a quien tiene más de una viva. Hoy pasa:
//     Maximiliano Cordova tiene TRES filas activadas a la vez —el modelo viejo,
//     donde un pack de tres meses creaba tres suscripciones de un mes, con
//     `bundle_seq` 1 y 2— que caducan el 31-oct, el 30-nov y el 31-dic. Un
//     alumno, tres filas. Desde la mig 315 el pack entrega UNA fila con
//     `term_months = 3`, pero las filas viejas siguen ahí: las dos formas
//     coexisten en los datos y lo seguirán haciendo.
//
//   · **Sumar `term_months`** (49) cuenta MESES como si fueran personas: una
//     suscripción de trimestre es un alumno, no tres.
//
//   · **Contar compradores de siempre** (16) incluye a quien ya caducó. Hay 8
//     filas `expired` y 1 `refunded`.
//
// Lo único correcto es `count(DISTINCT user_id)` sobre las vivas. Y «viva» es
// `status = 'activated'` Y `expires_at` en el futuro: `expires_at` es la fecha
// real de fin (mig 316, con CHECK de que una 'activated' nunca la tiene a
// NULL), sea el término de uno o de tres meses. No se deriva del mes como
// hacía el aviso de caducidad antes de arreglarlo: los trimestres tienen
// `period_month` de octubre y acaban en diciembre.

/** El único estado que significa «suscrito ahora mismo». */
export const ESTADO_SUSCRITO = 'activated';

export interface FilaDeSuscripcion {
  user_id: string | null;
  status: string;
  expires_at: string | null;
}

/**
 * Si esta compra está viva en `ahora`.
 *
 * `expires_at` a NULL NO cuenta. El CHECK de la mig 316 lo hace imposible en
 * una 'activated', así que una fila así sería un dato roto; darla por viva
 * sería inventarse una fecha de fin, y es justo el error que tenía el aviso de
 * caducidad. Si algún día apareciera, el número saldría corto —no inflado— y
 * eso se ve antes.
 */
export function estaSuscrita(f: FilaDeSuscripcion, ahora: Date): boolean {
  if (f.status !== ESTADO_SUSCRITO) return false;
  if (f.expires_at == null) return false;
  return new Date(f.expires_at).getTime() > ahora.getTime();
}

/** Personas distintas en un montón de filas. Las de `user_id` nulo no son nadie. */
export function alumnosDistintos(filas: readonly { user_id: string | null }[]): number {
  const gente = new Set<string>();
  for (const f of filas) if (f.user_id != null) gente.add(f.user_id);
  return gente.size;
}

/** Alumnos con al menos una suscripción viva. El KPI del dashboard. */
export function alumnosSuscritos(filas: readonly FilaDeSuscripcion[], ahora: Date): number {
  return alumnosDistintos(filas.filter((f) => estaSuscrita(f, ahora)));
}
